import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { CustomButton } from "src/design/CustomButton";
import {
  colorActionDanger,
  colorActionPrimaryText,
  colorTextError,
} from "src/design/tokens";
import { useRevokeSubscription } from "src/data/admin/queries/subscription";
import { AdminRestaurantSubscriptionDto } from "src/data/admin/types/subscription";

interface RevokeSubscriptionDialogProps {
  /** The subscription to revoke; `null` keeps the dialog closed. */
  subscription: AdminRestaurantSubscriptionDto | null;
  onClose: () => void;
  onRevoked: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * Revoking deletes the record, so access is cut immediately — the grace period
 * only covers a subscription that ran out on its own. The organization link and
 * staff assignments survive, so a later grant restores the restaurant as it was.
 */
export const RevokeSubscriptionDialog = ({
  subscription,
  onClose,
  onRevoked,
  onError,
}: RevokeSubscriptionDialogProps) => {
  const revokeMutation = useRevokeSubscription(() => {
    onRevoked(
      `${subscription?.restaurantName}'s subscription has been revoked.`,
    );
    onClose();
  });

  const handleRevoke = () => {
    if (!subscription) return;
    revokeMutation.mutate(subscription.restaurantId, {
      onError: (error) =>
        onError(
          error instanceof Error
            ? error.message
            : "Failed to revoke the subscription",
        ),
    });
  };

  return (
    <Dialog
      open={!!subscription}
      onClose={() => !revokeMutation.isPending && onClose()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>Revoke subscription</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Revoke <strong>{subscription?.restaurantName}</strong>'s subscription?
          Its staff lose access to the dashboard immediately — there is no grace
          period on a revoke — and the capabilities the tier granted are
          stripped. The organization link and staff assignments are kept, so
          granting a subscription again restores the restaurant as it was.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton
          color="inverted"
          onClick={onClose}
          disabled={revokeMutation.isPending}
        >
          Cancel
        </CustomButton>
        <CustomButton
          disabled={revokeMutation.isPending}
          onClick={handleRevoke}
          startIcon={
            revokeMutation.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{
            backgroundColor: colorActionDanger,
            color: colorActionPrimaryText,
            "&:hover": { backgroundColor: colorTextError },
          }}
        >
          Revoke
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
